import { ref } from "vue";
import { GenericController } from "@/stores/controller";
import type { IPageController } from "@/stores/types";

/**
 * Vue-facing runtime state for native pages. It also wires the legacy
 * GenericController so existing primitives and action handlers keep working.
 */
export function usePageRuntime(
  pageId: string,
  controller: IPageController = { view: pageId, data: {} },
) {
  activeModals.value = {};
  errorModal.value = false;
  loadingModal.value = false;
  iFrameModal.value = false;
  freeModal.value = false;

  GenericController.SetController(pageId, controller);
  const legacyController = GenericController.CurrentController();
  legacyController.setModalToggle((name, open) => {
    activeModals.value[name] = open;
  });
  legacyController.setErrorModalToggle((open) => {
    errorModal.value = open;
  });
  legacyController.setLoadingModal((open) => {
    loadingModal.value = open;
  });
  legacyController.setIFrameModal((open) => {
    iFrameModal.value = open;
  });
  legacyController.setFreeModal((open) => {
    freeModal.value = open;
  });

  const setModal = (name: string, open: boolean) =>
    legacyController.setModal(name, open);

  return {
    activeModals,
    errorModal,
    loadingModal,
    iFrameModal,
    freeModal,
    isModalOpen: (name: string) => activeModals.value[name] || false,
    openModal: (name: string) => setModal(name, true),
    closeModal: (name: string) => setModal(name, false),
    setLoading: (open: boolean) => legacyController.setLoading(open),
    setError: (open: boolean, code?: string, message?: string) =>
      legacyController.setErrorModal(open, code, message),
    setIFrame: (open: boolean) => legacyController.setIFrame(open),
    setFreeModal: (open: boolean) => legacyController.setFreeModalState(open),
  };
}

const activeModals = ref<Record<string, boolean>>({});
const errorModal = ref(false);
const loadingModal = ref(false);
const iFrameModal = ref(false);
const freeModal = ref(false);

/** Shared shell state used to render legacy action modals above native routes. */
export function useActivePageRuntime() {
  return { activeModals, errorModal, loadingModal, iFrameModal, freeModal };
}
